import { useEffect, useRef, useState } from "react";
import { api } from "../api";
import { actName, pathRef, shortAct, shortLabel } from "../format";
import {
  ApiError, type Citation, type DocumentDetail, type DocumentNode, type HistoryEntry,
} from "../types";

/**
 * The whole act behind a citation, scrolled to the cited provision.
 *
 * An excerpt cannot be verified on its own: a lawyer checking a citation
 * needs the surrounding article, the chapter it sits under, and whether
 * anything nearby qualifies it. So clicking a source opens the act rather
 * than expanding the snippet.
 *
 * Typeset to match legis.md, deliberately and in detail - Times New Roman at
 * 12pt, justified, a 34pt first-line indent on every paragraph, centred bold
 * chapter headings, and the article number bold with its title plain. Those
 * are the exact values from the source HTML (see platform/data/raw/*.html).
 * The reason is not nostalgia: an advocate checks a citation against the text
 * they already know by sight, and a re-typeset version invites the question
 * of whether it is also re-worded. Looking identical is the cheapest way to
 * say it is not.
 *
 * Anchoring is by `path`, not by matching text. A chunk can be a hard split
 * of a long article, so its text is not guaranteed to appear verbatim as any
 * single node's text - but its path always names a node.
 */

/** Rendered centred and bold, the way legis.md sets structural headings. */
const CENTRED_TYPES = new Set([
  "part",
  "title",
  "chapter",
  "section",
  "annex",
]);

const LABELS: Record<string, string> = {
  part: "Partea",
  title: "Titlul",
  chapter: "Capitolul",
  section: "Secțiunea",
  article: "Articolul",
  annex: "Anexa",
  preamble: "Preambul",
  signature: "Semnături",
};

/**
 * The marker an alineat or a point is written with, inline before its text:
 * "(3) Informaţiile precontractuale cuprind:", "a) tipul de credit".
 *
 * Inline because that is how the act reads, and because the marker is part
 * of the citation address - a lawyer writes "art. 5 alin. (3) lit. a)", so
 * the letter has to sit against the text it belongs to. Mirrors
 * Node.full_text() in the corpus parser, which builds the same markers when
 * assembling chunk text.
 */
function inlineMarker(n: DocumentNode): string | null {
  if (!n.number) return null;
  if (n.node_type === "paragraph") return `(${n.number})`;
  if (n.node_type === "point") {
    return /^\d+$/.test(n.number) ? `${n.number}.` : `${n.number})`;
  }
  return null;
}

/**
 * Which node the citation points at.
 *
 * Exact path first. Failing that, the longest node path that is a prefix of
 * the citation's - a chunk split below the node level (an alineat that was
 * itself too long) still lands the reader on the right article rather than
 * at the top of the act.
 */
function resolveTarget(nodes: DocumentNode[], path: string | null): string | null {
  if (!path) return null;
  const exact = nodes.find((n) => n.path === path);
  if (exact) return exact.node_id;

  let best: DocumentNode | null = null;
  for (const n of nodes) {
    if (path.startsWith(n.path) && (!best || n.path.length > best.path.length)) {
      best = n;
    }
  }
  return best?.node_id ?? null;
}

/** "din 12-07-2013", the form legis.md prints under the act number. */
function adoptionDate(iso: string | null): string {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return d ? `din ${d}-${m}-${y}` : "";
}

/**
 * Codes put a chapter's name on the line after its marker:
 *
 *     Capitolul I
 *     DISPOZIŢII GENERALE
 *
 * so the name parses as a plain text child, not as the chapter's heading.
 * legis.md centres and bolds both lines. Detected by shape - the first child
 * of a structural node, short, and upper-case - because the parser has
 * already lost the alignment the source carried.
 */
function isStructuralName(n: DocumentNode, prev: DocumentNode | null): boolean {
  if (n.node_type !== "text" || !n.text || n.text.length > 120) return false;
  if (!prev || !CENTRED_TYPES.has(prev.node_type)) return false;
  const letters = n.text.replace(/[^\p{L}]/gu, "");
  return letters.length > 0 && letters === letters.toLocaleUpperCase("ro");
}

function Node({
  n,
  isTarget,
  asName,
}: {
  n: DocumentNode;
  isTarget: boolean;
  asName: boolean;
}) {
  if (asName) {
    return (
      <p className="doc-p doc-centre">
        <strong>{n.text}</strong>
      </p>
    );
  }
  return <NodeBody n={n} isTarget={isTarget} />;
}

function NodeBody({ n, isTarget }: { n: DocumentNode; isTarget: boolean }) {
  const marker = inlineMarker(n);
  const centred = CENTRED_TYPES.has(n.node_type);
  const label = LABELS[n.node_type] ?? "";

  // Chapters and titles: the label and its heading are two centred bold
  // lines, exactly as the source sets them.
  if (centred) {
    return (
      <>
        {(label || n.number) && (
          <p className="doc-p doc-centre">
            <strong>{[label, n.number].filter(Boolean).join(" ")}</strong>
          </p>
        )}
        {n.heading && (
          <p className="doc-p doc-centre">
            <strong>{n.heading}</strong>
          </p>
        )}
        {n.text && <p className="doc-p">{n.text}</p>}
      </>
    );
  }

  // An article: "Articolul 5." bold, its title plain on the same line. The
  // split is legis.md's, and it matters - the number is the address, the
  // title is a convenience.
  if (n.node_type === "article") {
    return (
      <>
        <p className="doc-p">
          <strong>
            {LABELS.article} {n.number}.
          </strong>
          {n.heading ? ` ${n.heading}` : ""}
        </p>
        {n.text && <p className="doc-p">{n.text}</p>}
      </>
    );
  }

  if (!n.text && !marker) return null;

  return (
    <p className={`doc-p${isTarget ? " doc-target" : ""}`}>
      {marker ? `${marker} ` : ""}
      {n.text}
    </p>
  );
}


/**
 * Normalised for comparison: the archive's text carries non-breaking spaces,
 * soft line breaks and runs of whitespace that the extracted node text does
 * not, so a raw substring match finds nothing.
 */
function flatten(text: string): string {
  return text.replace(/\s+/gu, " ").replace(/ /gu, " ").trim();
}

/**
 * Highlight the cited passage inside the rendered original markup.
 *
 * The act's own HTML carries no paths, so the anchor cannot be looked up the
 * way it is in the node tree - it has to be found. The node's text came out
 * of this very markup, so an exact match on the opening words is reliable;
 * the fallback is to leave the document unmarked rather than highlight the
 * wrong paragraph, because a confident mark in the wrong place is worse than
 * none at all.
 *
 * Returns the element marked, for scrolling to.
 */
function markPassage(
  root: HTMLElement,
  needles: string[],
  article: string | null,
): HTMLElement | null {
  const blocks = Array.from(root.querySelectorAll<HTMLElement>("p, td, li, h4"));

  // Searched from the cited article's heading on, when it can be found:
  // the opening words of an alineat ("(1) Indemnizația se acordă…") recur
  // across articles, and the act's title above everything matched a passage
  // that began with it.
  let from = 0;
  let heading: HTMLElement | null = null;
  if (article) {
    const head = new RegExp(`^Articolul\\s+${escapeRe(article)}(?![\\d^])`, "u");
    const at = blocks.findIndex((el) => head.test(flatten(el.textContent ?? "")));
    if (at !== -1) {
      from = at;
      heading = blocks[at];
    }
  }

  for (const needle of needles) {
    const probe = flatten(needle).slice(0, 90);
    if (probe.length < 12) continue;
    for (const el of blocks.slice(from)) {
      if (flatten(el.textContent ?? "").includes(probe)) {
        el.classList.add("doc-hit");
        return el;
      }
    }
  }
  // The text did not match but the article did: land on it unmarked
  // rather than at the top of the act.
  return heading;
}

function escapeRe(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * What to look for in the original markup, best first: the cited node's
 * own text; for an article whose words are all in its alineate (its own
 * text is empty), the first of them; then the passage itself, without the
 * "<act> — în vigoare din …" line every passage opens with.
 */
function needlesFor(
  nodes: DocumentNode[],
  targetId: string | null,
  snippet: string,
): string[] {
  const out: string[] = [];
  const node = nodes.find((n) => n.node_id === targetId);
  if (node) {
    if (node.text.trim()) out.push(node.text);
    const child = nodes.find((n) => n.path.startsWith(`${node.path}/`) && n.text.trim());
    if (child) out.push(child.text);
  }
  const lines = snippet.split("\n");
  if (/ — în vigoare din /.test(lines[0] ?? "")) lines.shift();
  // The "Articolul 9. Heading" line is printed split differently in the
  // markup; the text under it is not.
  if (/^Articolul\s/.test(lines[0] ?? "")) lines.shift();
  const body = lines.join("\n").trim();
  if (body) out.push(body);
  return out;
}

export function DocumentPanel({
  citation,
  onClose,
}: {
  citation: Citation;
  onClose: () => void;
}) {
  const [doc, setDoc] = useState<DocumentDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  // "Istoricul versiunilor" swaps the act for the provision's wordings over
  // time. Loaded on first open: most readers never ask.
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState<HistoryEntry[] | null>(null);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const targetRef = useRef<HTMLDivElement | null>(null);
  const htmlRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let cancelled = false;
    setDoc(null);
    setError(null);
    api
      .document(citation.doc_id)
      .then((d) => {
        if (!cancelled) setDoc(d);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setError(
          e instanceof ApiError ? e.message : "Actul nu a putut fi încărcat.",
        );
      });
    return () => {
      cancelled = true;
    };
  }, [citation.doc_id]);

  const targetId = doc ? resolveTarget(doc.nodes, citation.path) : null;

  // Scroll after the nodes render, and centre rather than align to top so the
  // provision is read in context - what precedes it usually qualifies it.
  useEffect(() => {
    if (!doc || showHistory || !targetRef.current) return;
    targetRef.current.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [doc, targetId, showHistory]);

  // The original markup has no paths, so the passage has to be found in it
  // rather than looked up. Runs after the markup is in the DOM.
  useEffect(() => {
    if (!doc?.html || showHistory || !htmlRef.current) return;
    const article = /(?:^|\/)articolul-([^/]+)/.exec(citation.path ?? "")?.[1] ?? null;
    const hit = markPassage(
      htmlRef.current,
      needlesFor(doc.nodes, targetId, citation.snippet),
      article,
    );
    if (hit) hit.scrollIntoView({ block: "center", behavior: "smooth" });
    // Images the archive did not keep would show as broken icons.
    for (const img of Array.from(htmlRef.current.querySelectorAll("img"))) {
      img.addEventListener("error", () => img.remove(), { once: true });
    }
  }, [doc, targetId, citation.path, citation.snippet, showHistory]);

  // Escape closes, like any other overlay. The panel is a peek at a source,
  // not a place you navigate into.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  function toggleHistory() {
    const next = !showHistory;
    setShowHistory(next);
    if (next && history === null && doc) {
      setHistoryError(null);
      api
        .history(doc.family_id, citation.path)
        .then(setHistory)
        .catch((e: unknown) =>
          setHistoryError(e instanceof Error ? e.message : "Istoricul nu a putut fi încărcat."),
        );
    }
  }

  const status = !doc
    ? null
    : doc.repealed
      ? { cls: "bad", text: "abrogat" }
      : !doc.is_current
        ? { cls: "warn", text: "versiune anterioară" }
        : { cls: "ok", text: "în vigoare" };

  // "art. 15 alin. (1) · Cerințe privind clauzele contractuale": the
  // address from the path, the article's own title from the citation.
  const ref = pathRef(citation.path);
  const where = [ref ?? shortLabel(citation.label), ref ? citation.label : null]
    .filter((x) => x && x !== "Temei legal")
    .join(" · ");

  return (
    <aside className="src" aria-label="Textul actului">
      <div className="src-head">
        <div className="src-id">
          <h2>
            {doc
              ? actName(doc.act_type, doc.act_number, doc.title)
              : citation.title ? shortAct(citation.title) : "Se încarcă…"}
          </h2>
          <div className="src-sub">
            {status && <span className={`stat ${status.cls}`}>{status.text}</span>}
            <span>{where}</span>
          </div>
        </div>
        <button className="close" onClick={onClose} aria-label="Închide sursa">
          ×
        </button>
      </div>

      {doc && (doc.repealed || !doc.is_current) && (
        // Repealed text presented as current is the one mistake this
        // product cannot make; the badge alone is too quiet for it.
        <p className="src-warn">
          {doc.repealed
            ? "Acest act a fost abrogat."
            : "Aceasta nu este versiunea în vigoare."}
        </p>
      )}

      <div className="src-body">
        {error && <p className="src-error">{error}</p>}
        {!doc && !error && <p className="muted">se încarcă actul…</p>}

        {showHistory && (
          <History entries={history} error={historyError} />
        )}

        {!showHistory && doc?.html && (
          // Sanitised server side in legis.document_html: an allowlist of
          // tags, attributes and CSS properties, links forced absolute with
          // rel="noopener". The colours, the coat of arms and the links to
          // amending laws are the document.
          <div
            className="doc doc-original"
            ref={htmlRef}
            dangerouslySetInnerHTML={{ __html: doc.html }}
          />
        )}

        {!showHistory && doc && !doc.html && (
          <div className="doc">
            {/* The masthead legis.md prints above every act. */}
            <div className="doc-masthead">
              <p>Republica Moldova</p>
              {doc.issuing_authority && (
                <p>
                  <strong>{doc.issuing_authority}</strong>
                </p>
              )}
              <p>
                <strong>{doc.act_type}</strong>
                {doc.act_number ? ` Nr. ${doc.act_number}` : ""}
                <br />
                {adoptionDate(doc.adoption_date)}
              </p>
              <p>
                <strong>{doc.title}</strong>
              </p>
            </div>
            {doc.publication_source && (
              <p className="doc-publicat">{doc.publication_source}</p>
            )}

            {doc.nodes.map((n, i) => {
              const isTarget = n.node_id === targetId;
              return (
                <div
                  key={n.node_id}
                  ref={isTarget ? targetRef : undefined}
                  className={isTarget ? "doc-hit" : undefined}
                >
                  <Node
                    n={n}
                    isTarget={isTarget}
                    asName={isStructuralName(n, doc.nodes[i - 1] ?? null)}
                  />
                </div>
              );
            })}
          </div>
        )}

        {!showHistory && doc && citation.path && !targetId && (
          // Better to say so than to leave the reader hunting for a highlight
          // that was never applied.
          <p className="src-error">
            Pasajul citat nu a putut fi localizat exact în text.
          </p>
        )}
      </div>

      <div className="src-foot">
        {doc && (
          <a
            className="ghost"
            href={`https://www.legis.md/cautare/getResults?doc_id=${encodeURIComponent(doc.doc_id)}`}
            target="_blank"
            rel="noreferrer"
          >
            Deschide pe legis.md
          </a>
        )}
        <button
          className={showHistory ? "ghost on" : "ghost"}
          onClick={toggleHistory}
          disabled={!doc}
          aria-pressed={showHistory}
        >
          Istoricul versiunilor
        </button>
      </div>
    </aside>
  );
}

/** Every wording the provision has had, newest first, each with its window. */
function History({
  entries,
  error,
}: {
  entries: HistoryEntry[] | null;
  error: string | null;
}) {
  if (error) return <p className="src-error">{error}</p>;
  if (entries === null) return <p className="muted">se încarcă istoricul…</p>;
  if (!entries.length) {
    return (
      <p className="muted">
        Nu avem versiuni anterioare ale acestei prevederi în arhivă.
      </p>
    );
  }
  const newest = entries.slice().reverse();
  return (
    <ol className="history">
      {newest.map((e, i) => (
        <li key={`${e.path}-${e.effective_from}-${i}`}>
          <div className="history-when">
            <strong>
              {e.effective_to
                ? `${e.effective_from ?? "?"} – ${e.effective_to}`
                : `din ${e.effective_from ?? "?"}`}
            </strong>
            {!e.effective_to && !e.repealed && <span className="stat ok">în vigoare</span>}
            {e.repealed && <span className="stat bad">abrogat</span>}
          </div>
          {(e.number || e.heading) && (
            <p className="history-label">
              {[e.number, e.heading].filter(Boolean).join(" · ")}
            </p>
          )}
          <p className="history-text">{e.text}</p>
        </li>
      ))}
    </ol>
  );
}
